// The report catalogue (SPEC.md §4).
//
// Each type has its own parameter set, modelled as data rather than bespoke
// forms: the shapes overlap heavily (nearly all take a date range and a
// format), so a new type is an entry here rather than a new screen.
//
// Reports are produced artifacts, not live views — you generate one and it is
// kept, with the parameters it was run with. That matters for tax: a report
// filed with an accountant has to keep saying what it said.

import { todayISO } from './clock.js';
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';
import { paramRows } from './reportRunner.js';

export const REPORT_FORMATS = ['PDF', 'CSV (Excel)'];

// The catalogue's own literal, pinned to the core's (`test/core/reports.wasm.test.js`)
// — what a build with no report PDF service configured shows: PDF offered
// because MMS offers it (see `reportFormatParam` below), and disabled rather
// than silently handing over a .csv to someone who asked for a PDF.
export const UNAVAILABLE_FORMATS = { PDF: 'Not available yet — reports export as CSV' };

// Parameter primitives the forms are assembled from.
const dateRange = { id: 'dateRange', label: 'Date Range', type: 'dateRange', required: true };
const format = {
  id: 'format',
  label: 'Report Format',
  type: 'radio',
  options: REPORT_FORMATS,
  unavailable: UNAVAILABLE_FORMATS,
  // The catalogue's literal, for a caller that says nothing about PDF. Every
  // page renders `reportFormatParam` instead — see the note there.
  default: 'CSV (Excel)',
};

// The Report Format parameter as the *build* makes it (#485), not the
// catalogue's unconfigured literal above: PDF selectable — and the default, as
// MMS opens Create Report with PDF picked
// (`research/mms-capture/03-modals/rpt-05-revenue-expense-params-bottom.jpg`) —
// once a report PDF service is named; otherwise CSV only, with PDF shown
// disabled and why. Nobody is defaulted into a format that fails.
//
// The server renders every PDF (`documentLink`, #621), so ReportsPage passes
// `true`; the parameter is there because the rule is the core's, and the
// phones ask it the same way (#447, #485).
export function reportFormatParam(pdfAvailable) {
  const reason = coreSync().reportPdfUnavailableReason(Boolean(pdfAvailable));
  return { ...format, unavailable: reason ? { PDF: reason } : {} };
}
// The core's `ATTACHMENTS_HINT`, pinned to it with the rest of the catalogue
// (test/core/reports.wasm.test.js).
export const REPORT_ATTACHMENTS_HINT = "Adds each entry's receipts to the end of the PDF. A CSV has none.";
const note = { id: 'note', label: 'Note', type: 'textarea', hint: 'Private — does not appear in the report file' };
// MMS's Students control (#795): a multi-select of the roster whose value is
// the chosen students' ids, reading "All Students" while empty. What it lists
// and its Quick Select are `reportStudentChoices` and
// `reportStudentQuickSelects` below.
const students = { id: 'students', label: 'Students', type: 'students', hint: 'All Students' };
// Attendance's and Lessons Provided's Categories: the studio's lesson
// categories, by label; ReportsPage hands their labels in as `options`.
const eventCategories = { id: 'categories', label: 'Categories', type: 'eventCategories', hint: 'All categories' };

export const REPORT_TYPES = [
  {
    id: 'attendance', label: 'Attendance',
    description: 'Every lesson in a period, a line per student, with the attendance taken.',
    params: [students, dateRange,
      { id: 'includeNotes', label: 'Include Notes', type: 'checklist', options: ['Student', 'Parents', 'Private'] },
      eventCategories, format, note],
  },
  {
    id: 'billable_hours', label: 'Billable Hours',
    description: 'Hours of billable lessons in a period: in total, by event category, or lesson by lesson.',
    params: [dateRange,
      { id: 'detailLevel', label: 'Detail Level', type: 'radio',
        options: ['Teacher', 'Event Category', 'Individual Events'], default: 'Teacher' },
      format, note],
  },
  {
    id: 'family_balances', label: 'Family Balances',
    description: 'What each family owes, as of a date.',
    params: [
      { id: 'asOf', label: 'As of', type: 'date' },
      { id: 'onlyOwing', label: 'Only families with a balance owing', type: 'checkbox', default: true },
      format, note],
  },
  {
    id: 'lessons_provided', label: 'Lessons Provided',
    description: 'Lessons provided in a period, counted or in hours, by day, week, month, quarter, year or student.',
    params: [students, dateRange, eventCategories,
      { id: 'groupBy', label: 'Group By', type: 'radio',
        options: ['Day', 'Week', 'Month', 'Quarter', 'Year', 'Student'], default: 'Month' },
      { id: 'sumBy', label: 'Sum By', type: 'radio', options: ['Lessons', 'Hours'], default: 'Lessons' },
      format, note],
  },
  {
    id: 'makeup_credits', label: 'Make-Up Credits',
    description: 'Make-up credits issued and used in a period, and the balance they leave.',
    params: [students, dateRange,
      { id: 'sortBy', label: 'Sort By', type: 'radio',
        options: ['Student', 'Event Category', 'Individual Events'], default: 'Student' },
      { id: 'openingBalance', label: 'Include make-up credits from before start date', type: 'checkbox' },
      format, note],
  },
  {
    id: 'mileage', label: 'Mileage',
    description: 'Logged travel for tax deduction.',
    params: [dateRange, format, note],
  },
  {
    id: 'practice_log', label: 'Practice Log Summary',
    description: 'Practice time and entries logged by each student in a period.',
    params: [students, dateRange,
      { id: 'sortBy', label: 'Sort By', type: 'radio',
        options: ['Total Time', 'Number of Entries', 'Student Name'], default: 'Total Time' },
      format, note],
  },
  {
    id: 'repertoire', label: 'Repertoire',
    description: "Each student's pieces with the chosen statuses, and their notes when asked for.",
    params: [students,
      { id: 'status', label: 'Status', type: 'checklist', hint: 'All statuses',
        options: ['Assigned', 'In Progress', 'Polishing', 'Completed'], default: ['In Progress', 'Completed'] },
      { id: 'repertoireNotes', label: 'Include repertoire notes', type: 'checkbox' },
      format, note],
  },
  {
    id: 'revenue_expenses', label: 'Revenue & Expenses',
    description: 'Income and outgoings, itemised or summarised.',
    params: [
      { id: 'reportOn', label: 'Report On', type: 'radio',
        options: ['Revenue & Expense', 'Revenue', 'Expense'], default: 'Revenue & Expense' },
      // Cash vs accrual is the parameter that makes this a real accounting
      // report rather than a total: cash counts money when it moves, accrual
      // counts it when it is earned, and a studio's tax filing depends on
      // which basis it is on.
      { id: 'basis', label: 'Basis', type: 'radio', options: ['Cash', 'Accrual'], default: 'Cash' },
      // Multi-selects (#601): the core filters on whichever values are
      // chosen, "all" when none are — ParamField renders the checklist from
      // `revenueExpenseCategories`/`revenueExpensePayees`, computed in
      // ReportsPage from data the page already holds, and handed to these
      // two through `options` the same way `reportFormatParam` hands Report
      // Format its availability.
      { id: 'categories', label: 'Categories', type: 'categories', hint: 'All categories' },
      { id: 'payees', label: 'Payees/Payers', type: 'payees', hint: 'All payees/payers' },
      dateRange,
      { id: 'sortBy', label: 'Sort By', type: 'radio',
        options: ['Date', 'Payee/Payer', 'Category', 'Payment Type'], default: 'Date' },
      { id: 'mode', label: 'Mode', type: 'radio', options: ['Itemized', 'Summary'], default: 'Itemized' },
      // #623: the PDF appends each listed entry's receipts (`attached_entries`
      // in core/src/reports.rs, drawn by services/report-pdf). Offered
      // whatever the Report Format, so the hint says a CSV has none.
      { id: 'attachments', label: 'Include Attachments', type: 'checkbox', default: false, hint: REPORT_ATTACHMENTS_HINT },
      format, note],
  },
  {
    id: 'sales_tax', label: 'Sales Tax',
    description: 'Tax collected over a period.',
    params: [dateRange, format, note],
  },
  {
    id: 'student_details', label: 'Student Details Report',
    description: "Each student's status, lesson and price, with their family contact's email and phone.",
    // No Report Format, as MMS's form has none: `defaultParams` states PDF.
    params: [students,
      { id: 'include', label: 'Include', type: 'checklist', options: ['Note about students', 'Note about parents'] },
      note],
  },
  {
    id: 'retention', label: 'Student Retention Rate',
    description: 'Starts, stops and net change over a period.',
    params: [dateRange, format, note],
  },
  {
    id: 'tax_credit_receipt', label: 'Tax Credit Receipt',
    description: 'Per-family receipt of fees paid, for claiming a tax credit.',
    params: [dateRange, format, note],
  },
];

export function getReportType(id) {
  return REPORT_TYPES.find((r) => r.id === id) || null;
}

// Default parameter values for a type, so the form opens ready to run.
//
// `balanceDate` is the studio's Default Balance Date, already resolved to a
// date-only string by domain/clock.js — that setting exists precisely so
// a studio that strikes its balances on the last of the month does not retype
// the date on every Family Balances report. Optional, because this module is
// pure and a caller without a studio record (a test, the catalogue's own
// coverage test) still has to get a usable default.
//
// `today` is the date the form opens on — `todayISO()` unless a caller (a
// test, a phone replaying this) names one; the current month is its month.
//
// `pdfAvailable` (#485) decides the Report Format parameter's default — PDF
// once a report PDF service is named, else CSV, the same rule
// `reportFormatParam` applies to the radio's availability. Defaults to
// `false`.
export function defaultParams(typeId, { balanceDate, today = todayISO(), pdfAvailable = false } = {}) {
  return documentFromRows(coreSync().reportDefaultParams(
    String(typeId ?? ''), balanceDate ? String(balanceDate) : null, String(today), Boolean(pdfAvailable),
  ));
}

// The month `today` falls in, as a range — `current_month_range` in the core.
export function currentMonthRange(today = todayISO()) {
  return coreSync().reportCurrentMonthRange(String(today));
}

// The parameters a report was run with, as a document would state them.
//
// A report is a produced artifact — a table filed with an accountant is
// uninterpretable a year later without the as-of date or range it was taken on.
// Built here rather than in the PDF service because only the catalogue knows
// the labels ("As of", not "As Of") and the hints.
//
// Two omissions:
//   - `note` is described above as private and "does not appear in the report
//     file". A report file is exactly what this feeds.
//   - `format` is the document you are holding.
//
// An empty value is stated as its hint rather than dropped: "Students: All
// students" is information, whereas a missing line reads as an oversight.
//
// Its dates — a range's two ends, an As of date — are the studio's short date
// (`datePattern`, `shortDatePattern`), as MMS's report writes `8/1/2026 to
// 8/31/2026` for a US studio (research/mms-capture/08-report-pdf).
export function describeParams(typeId, params = {}, datePattern) {
  return coreSync().reportDescribeParams(String(typeId ?? ''), paramRows(params), String(datePattern ?? ''))
    .map(({ id, label, value }) => ({ id, label, value }));
}

// A report over an unbounded range cannot be trusted against an indefinite
// series, so the runner refuses rather than truncating.
export function rangeIsBounded(v) {
  return coreSync().reportRangeIsBounded(v ? String(v) : null);
}

// ─── The report list, the result drawer and the request — shared by
// ReportTable.jsx, ReportResultDrawer.jsx, data/useReports.jsx and the
// phones ─────────────────────────────────────────────────────────────────────

// The status pill on a row. A request that is neither ready nor refused is
// still being made: MMS's own `Processing ⟳`.
export function reportStatusLabel(status) {
  return coreSync().reportStatusLabel(status ? String(status) : null);
}

// What the Format column says.
//
// A stored PDF request is marked rather than rewritten where PDF cannot be
// had: the studio *asked* for PDF and the request is part of the record.
//
// `pdfAvailable` is what the Create Report form is given (#619): "— exported
// as CSV" only where PDF cannot be had. On the web that is nowhere
// (ReportsPage passes `true`, #621); a stored PDF request reads as PDF.
export function reportFormatCell(format, pdfAvailable = false) {
  return coreSync().reportFormatCell(format ? String(format) : null, pdfAvailable === true);
}

// The Report Date Range and Date Created columns, in the studio's short date
// (`datePattern`), and the Note column; a dash for none.
export const reportDateRangeCell = (params, datePattern) => coreSync().reportDateRangeCell(
  params?.dateRange ? String(params.dateRange) : null, String(datePattern ?? ''),
);
export const reportNoteCell = (params) => coreSync().reportDashCell(params?.note ? String(params.note) : null);
export const reportCreatedCell = (createdOn, datePattern) => coreSync().reportDateCell(
  createdOn ? String(createdOn) : null, String(datePattern ?? ''),
);

// The list, newest first by the business date each was asked for on.
//
// A stable sort of the reversed list; one with no `createdOn` sorts last.
export function sortReports(reports = []) {
  const list = [...reports];
  return coreSync().sortReports(list.map((r) => (r?.createdOn ? String(r.createdOn) : '')))
    .map((i) => list[i]);
}

// The line under a finished report's title: when, on which basis, over what,
// and how many rows — the dates in the studio's short date (`datePattern`).
export function reportResultMeta(report, rowCount, datePattern) {
  return coreSync().reportResultMeta(
    report?.createdOn ? String(report.createdOn) : null,
    report?.result?.basis ? String(report.result.basis) : null,
    report?.params?.dateRange ? String(report.params.dateRange) : null,
    Number(rowCount) || 0,
    String(datePattern ?? ''),
  );
}

// The file a report downloads as: its label, dashed and lower-cased, and the
// date it was asked for. `ext` is `csv`, or `pdf` for the fallback the PDF
// service's own name replaces.
export function reportFilename(label, createdOn, ext = 'csv') {
  return coreSync().reportFilename(label ? String(label) : null, String(createdOn), String(ext));
}

// The request document `createReport` writes (without `createdAt`, the
// server's), from what the runner answered. Ready with its table, or refused
// with its reason; `createdOn` is the business date, so the list orders before
// the server's timestamp resolves.
//
// The core writes it as rows but `result.rows`, a list of maps inside a map,
// which is the table itself — the runner's rows, added here — and
// `createdAt`, left to the caller's sentinel (`serverTimestamp`, or none).
export function reportRequestDocument({ typeId, label, params, result, createdOn }, serverTimestamp = undefined) {
  const rows = coreSync().reportRequestDocument(
    String(typeId ?? ''), String(label ?? ''), paramRows(params),
    { supported: Boolean(result?.supported), basis: result?.basis ?? null, reason: result?.reason ?? null },
    (result?.columns || []).map(String), String(createdOn),
  );
  const doc = documentFromRows(
    serverTimestamp === undefined ? rows.filter((r) => r.field !== 'createdAt') : rows, serverTimestamp,
  );
  if (doc.result) doc.result = { columns: doc.result.columns, rows: result.rows || [], basis: doc.result.basis };
  return doc;
}

// The Business Reports screen's words, by code.
export const REPORTS_WORDING = Object.freeze({
  pageTitle: 'Business Reports',
  createButton: '+ Create Report',
  emptyTitle: "You haven't created any reports yet",
  emptyDescription: 'Customize and download reports to view revenue & expenses, lessons taught, student retention, and more.',
  createTitle: 'Create Report',
  reportType: 'Report Type',
  selectType: 'Select\u2026',
  cancel: 'Cancel',
  create: 'Create',
  columnType: 'Report Type',
  columnCreated: 'Date Created',
  columnFormat: 'Format',
  columnRange: 'Report Date Range',
  columnNote: 'Note',
  close: 'Close',
  downloadPdf: 'Download PDF',
  downloadCsv: 'Download CSV',
  noRows: "No data matched this report's parameters.",
  pdfFailed: 'The report could not be downloaded as a PDF.',
  rangeTo: 'to',
  studentsSearch: 'Search students',
  clearSelection: 'Clear Selection',
  quickSelect: 'Quick Select',
  includeInactive: 'Include inactive students in list',
});

// A date range's first box: "Date Range — from".
export const reportRangeFromLabel = (label) => coreSync().reportRangeFromLabel(String(label ?? ''));

// ─── The Students control (#795) ───────────────────────────────────────────

const choiceStudent = (s) => ({
  id: String(s?.id ?? ''),
  firstName: s?.firstName == null ? null : String(s.firstName),
  lastName: s?.lastName == null ? null : String(s.lastName),
  status: s?.status == null ? null : String(s.status),
});

// The students the control lists — `{ id, name, inactive }`, by name in the
// studio's Naming Format (`studentFormat`), the inactive ones only with
// `includeInactive` (MMS's "Include inactive students in list").
export function reportStudentChoices(students = [], studentFormat = null, includeInactive = false) {
  return coreSync().reportStudentChoices(
    students.map(choiceStudent), studentFormat == null ? null : String(studentFormat), Boolean(includeInactive),
  ).map(({ id, name, inactive }) => ({ id, name, inactive }));
}

// Its Quick Select — `{ label, studentIds }`: Active Students, Trial
// Students, the teacher's Active & Trial Students (`teacherName`, already in
// the studio's Teacher Name Format), then each instrument tag. `tags` are the
// tags collection's documents, each holding its own `studentIds`.
export function reportStudentQuickSelects(students = [], tags = [], teacherName = null) {
  return coreSync().reportStudentQuickSelects(
    students.map(choiceStudent),
    tags.map((t) => ({
      label: String(t?.label ?? ''),
      kind: t?.kind == null ? null : String(t.kind),
      studentIds: (Array.isArray(t?.studentIds) ? t.studentIds : []).map(String),
    })),
    teacherName == null ? null : String(teacherName),
  ).map(({ label, studentIds }) => ({ label, studentIds }));
}
